// NATIVE IMPORTS
import "./Applicants.scss";
import { Collapse, Table, Tooltip } from "antd";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "src/hooks/redux";

// SERVICES
import LocalStorageService from "../../services/LocalStorageService";
import { getNotificationMessageForApplicant } from "../../services/NotificationFormattingService";
import {
  getRemainingTimeInMs,
  isApplicationHoldClosed,
  isApplicationStatusExpired,
  isClockExpired,
  isCancelledDueToHighRisk,
} from "../../utils/HoldClock";

// CUSTOM COMPONENTS
import {
  getApplicantPaymentStatus,
  getApplicationTermsAndConditions,
  getCommsPreferencesByApplicantIds,
  getIsFurtherReviewFeatureEnabled,
  setApplicantPaymentAndTermsFromLeaseDomain,
  setCommsPreferencesFromLeaseDomainApplicants,
  fetchLeaseAddendums,
  getApplicationById,
} from "../../services/AdminDashService";
import HoldClockStatus from "../Shared/HoldClockStatus";
import ApplicantInformationModal from "./ApplicantInformationModal";
import SubmitAddendumOnBehalfModal from "./SubmitAddendumOnBehalfModal";
import { ApplicationAddendum } from "src/types/LeaseDomainAddendum";
import { indexPendingAddendumsByApplicantId } from "src/utils/LeaseDomainAddendum";

import { Applicant, ApplicantType } from "../../types/Applicant";
import {
  ApplicationStatusId,
  ApplicationStatusText,
  ReduxApplication,
} from "../../types/Application";
import {
  formatPhone,
  getGenderEquivalent,
  getRiskLevel,
  getStatusName,
  isApplicationEditable,
  isLeaseDomainApplicationId,
} from "../../utils/Utils";
import CancelApplicationModal from "./CancelApplicationModal";
import Chip from "@mui/material/Chip";
import InformationIcon from "../Shared/icons/InformationIcon";
import { Configuration } from "src/types/Redux";
import {
  hasUnresolvedHighRisk,
  getActualLeaseStatus,
  getApplicantBaseStatusName,
} from "src/utils/Application";
import { ApplicationStatus } from "src/types/ApplicationStatus";

const Applicants = ({
  application,
  configurationData,
  isConfigurationLoaded,
}: {
  application: ReduxApplication;
  configurationData: Configuration;
  isConfigurationLoaded: boolean;
}) => {
  const { id } = useParams();
  const [applicantData, setApplicantData]: any = useState();
  const [guarantorsList, setGuarantorsList]: any = useState([]);
  const [dependentsList, setDependentsList]: any = useState([]);
  const [modalShow, setModalShow] = useState(false);
  const [modalApplicant, setModalApplicant] = useState({});
  const [columns, setColumns] = useState([]);
  const [selectedApplication, setSelectedApplication]: any = useState();
  const [selectedApplicant, setSelectedApplicant]: any = useState();
  const [hasFetched, setHasFetched] = useState(false);
  const [addendumCatalog, setAddendumCatalog] = useState<ApplicationAddendum[]>(
    []
  );
  const [addendumModalShow, setAddendumModalShow] = useState(false);
  const [addendumModalApplicant, setAddendumModalApplicant] = useState<{
    id: string;
    name: string;
  }>({ id: "", name: "" });
  const addendumForms = application?.addendums?.forms;

  const isAddendumsEnabled = (addendumForms ?? []).length > 0;
  const hasAddendumResponse = addendumForms?.some(
    (addendumForm) => addendumForm?.responses?.length
  );

  const { Panel } = Collapse;
  const dispatch = useAppDispatch();
  // Tracks which lease the addendum catalog belongs to, so a fetch that resolves after the
  // user has navigated to a different application (mount effect below, or the on-behalf submit
  // refresh) can't overwrite the catalog with a stale lease's data.
  const currentLeaseIdRef = useRef(application?.leaseId);
  currentLeaseIdRef.current = application?.leaseId;

  useEffect(() => {
    // prepareData/setSelectedApplication/setDependentsList don't read configurationData -
    // no need to gate on it here.
    if (!application) return;
    prepareData(application.applicants);
    setSelectedApplication(application);
    setDependentsList(formatData(application.dependents));
  }, [application, configurationData]);

  useEffect(() => {
    if (
      !application?.leaseId ||
      !isLeaseDomainApplicationId(String(application?.applicationId ?? ""))
    ) {
      setAddendumCatalog([]);
      return;
    }

    const leaseId = application.leaseId;
    let cancelled = false;
    (async () => {
      const addendums = await dispatch(fetchLeaseAddendums(leaseId));
      if (!cancelled && currentLeaseIdRef.current === leaseId) {
        setAddendumCatalog(addendums ?? []);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [application?.leaseId, application?.applicationId]);

  // Re-derive the Adults action column once the addendum catalog resolves - it loads
  // asynchronously after the initial prepareData/prepColumns pass below, so the "Answer
  // Addendum" item wouldn't otherwise appear until some unrelated re-render.
  // Deliberately keyed on addendumCatalog alone, not applicantData - reading applicantData.length
  // here doesn't need to also retrigger this on every applicantData identity change; the catalog
  // resolving is the only thing this effect exists to react to.
  useEffect(() => {
    if (!applicantData?.length) return;
    prepColumns(applicantData.length);
  }, [addendumCatalog]);

  useEffect(() => {
    if (!applicantData || !guarantorsList || !dependentsList) {
      return;
    }
    if (hasFetched) return;

    // Lease Domain-routed application (see isLeaseDomainApplicationId): smsPreference already
    // came back on application.applicants from the single get-application-by-id call - no need
    // for (and no working route for) the legacy per-applicant communication proxy below.
    if (isLeaseDomainApplicationId(String(application?.applicationId ?? ""))) {
      if (application?.applicants?.length) {
        dispatch(
          setCommsPreferencesFromLeaseDomainApplicants(application.applicants)
        );
        setHasFetched(true);
      }
      return;
    }

    const applicantIds: string[] = [
      ...applicantData?.map((a: { id: string }) => a?.id).filter(Boolean),
      ...guarantorsList?.map((g: { id: string }) => g?.id).filter(Boolean),
    ];
    const uniqueApplicantIds = Array.from(new Set(applicantIds));

    if (uniqueApplicantIds?.length > 0) {
      dispatch(getCommsPreferencesByApplicantIds(uniqueApplicantIds));
      setHasFetched(true);
    }
  }, [applicantData, guarantorsList, dependentsList, application]);

  /*
   * Defines the Custom Toggle for the React Dropdown Component
   * See Reference:
   * - https://react-bootstrap.github.io/components/dropdowns/
   * - https://stackoverflow.com/questions/61433318/explanation-for-this-complicated-is-not-assignable-to-type-error-in-typescript
   */

  const CustomToggle = React.forwardRef<HTMLDivElement>((props, ref) => (
    <div ref={ref} {...props}>
      <span className="meatball-menu">...</span>
    </div>
  ));

  const riskColumn = {
    title: "Risk",
    dataIndex: "riskLevel",
    width: 120,
    align: "center",
    render: (_riskLevel, record) => {
      const hasRisk =
        record?.riskLevel &&
        record?.riskColor &&
        record?.riskPercentage !== undefined &&
        record?.riskPercentage !== null;

      if (!hasRisk) return null;
      return (
        <Tooltip
          title={record.riskLevel}
          showArrow={false}
          overlayInnerStyle={{
            backgroundColor: "#fff",
            color: "#000",
            border: "1px solid #000",
            padding: "3px 2px",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <InformationIcon
              className="information-icon"
              color={record.riskColor}
            />
          </span>
        </Tooltip>
      );
    },
  };

  const isFurtherReviewEnabled = getIsFurtherReviewFeatureEnabled(
    configurationData?.features,
    application?.communityId
  );

  const getColumns = (
    title: string,
    itemCount: number,
    hasGuarantors = true
  ) => [
    {
      title: `${title} (${itemCount})`,
      dataIndex: "fullName",
      width: 180,
      className: "avb-fullname-col",
    },
    ...(title !== "Dependents" && hasGuarantors && isFurtherReviewEnabled
      ? [riskColumn]
      : []),
    {
      title: title === "Adults" ? "Status" : "",
      dataIndex: "status",
      width: 180,
      className: "avb-status-col",
      render: (status, applicant) => {
        const { applicationHold, applicationStatusId } = application;
        // AP2-6841: a closed hold (cancelled/screening) is terminal, never re-evaluated against today's date
        const isHoldClock = Boolean(
          applicationHold &&
            applicationHold.holdDate &&
            !isApplicationHoldClosed(applicationHold)
        );
        let remainingTime: number | null | undefined;

        // 🔹 Handle hold clock expiry
        if (isHoldClock && status !== "Expired") {
          const holdDateFormatted = applicationHold?.holdDate
            ? new Date(applicationHold.holdDate)
            : null;
          remainingTime = holdDateFormatted
            ? getRemainingTimeInMs(holdDateFormatted)
            : null;
          if (
            status === "In Progress" &&
            remainingTime !== null &&
            isClockExpired(remainingTime)
          ) {
            status = "Expired";
          }
        }
        const isExcluded =
          applicant?.isOnExclusionList &&
          applicationStatusId === ApplicationStatusId.Cancelled;
        const hasHighRiskCancellationEmailSent =
          applicant?.riskAssessmentCancellationTemplate;
        return (
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <HoldClockStatus status={status} remainingTime={remainingTime} />
            {isExcluded && (
              <Tooltip
                title={applicant?.exclusionReason || "No reason provided"}
                placement="bottom"
                showArrow={false}
                overlayInnerStyle={{
                  backgroundColor: "#fff",
                  color: "#000",
                  border: "1px solid #000",
                  padding: "3px 2px",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <InformationIcon className="information-icon" color="#000" />
                </span>
              </Tooltip>
            )}
            {hasHighRiskCancellationEmailSent && (
              <Tooltip
                title={getCancellationTemplateEmailTooltip(
                  applicant?.riskAssessmentCancellationTemplate
                )}
                placement="bottom"
                showArrow={false}
                overlayInnerStyle={{
                  backgroundColor: "#fff",
                  color: "#000",
                  border: "1px solid #000",
                  padding: "3px 2px",
                }}
              >
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                  }}
                >
                  <InformationIcon className="information-icon" color="#000" />
                </span>
              </Tooltip>
            )}
          </span>
        );
      },
    },
    {
      title: title == "Adults" ? "Email" : "",
      dataIndex: "emailAddress",
      width: 180,
      className: "avb-email-col",
      render: (email) => <span title={email}>{email}</span>,
    },
    {
      title: title == "Adults" ? "Phone" : "",
      dataIndex: "phoneNumber",
      width: 140,
      className: "avb-phone-col",
      render: (phone: string, record: { id: string }) => (
        <PhoneCellWithSmsTooltip phone={phone} applicantId={record.id} />
      ),
    },
  ];

  const getCancellationTemplateEmailTooltip = (
    templateName: string
  ): string => {
    const defaultMessage = "High risk cancellation email sent";

    if (!templateName) {
      return defaultMessage;
    }

    const templates =
      configurationData?.features?.communication
        ?.highRiskCancellationEmailTemplate;

    if (!templates) {
      return defaultMessage;
    }

    const template =
      templates[templateName as keyof typeof templates] || defaultMessage;

    const communityName = application?.communityName ?? "";

    return template.replace("[communityName]", communityName);
  };

  const prepColumns = (itemCount: number) => {
    const applicationData = application ?? {};
    const showSendNotificationAction =
      applicationData.applicationStatusId !== ApplicationStatusId.LeaseSigned &&
      applicationData.applicationStatusId !== ApplicationStatusId.LeasePending;
    const col: any = getColumns("Adults", itemCount);
    col.push({
      title: "",
      width: 10,
      className: "avb-action-col",
      render: (text, record) => {
        const isEditable = isApplicationEditable(record);
        const pendingAddendums = getPendingAddendumsForApplicant(
          String(record.id)
        );
        return (
          <Dropdown>
            <Dropdown.Toggle
              as={CustomToggle}
              className="avb-context"
              style={{ cursor: "pointer" }}
            >
              ...
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {showSendNotificationAction && (
                <Dropdown.Item
                  eventKey="2"
                  onClick={() => onSendNotificationClick(record)}
                >
                  Send Notification
                </Dropdown.Item>
              )}
              {pendingAddendums.length > 0 && (
                <Dropdown.Item
                  eventKey="3"
                  onClick={() => onAnswerAddendumClick(record)}
                >
                  Answer Addendum
                </Dropdown.Item>
              )}
              <Dropdown.Item
                eventKey="1"
                onClick={() => onViewApplicationClick(record)}
              >
                {isEditable ? "View / Edit" : "View"} Application
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    });

    setColumns(col);
  };

  const getGuarantorColumns = (title: string, itemCount: number) => {
    if (title === "Dependents") {
      return [
        {
          title: `${title} (${itemCount})`,
          dataIndex: "fullName",
          width: 180,
          className: "avb-fullname-col",
        },
      ];
    }

    const col: any = getColumns(title, itemCount, itemCount > 0);

    col.push({
      title: "",
      width: 10,
      className: "avb-action-col",
      render: (text, record) => {
        const isEditable =
          record.emailAddress &&
          (record.nonUSPhone == true ||
            (record.phoneNumber && record.phoneNumberType));

        return (
          <Dropdown>
            <Dropdown.Toggle
              as={CustomToggle}
              className="avb-context"
              style={{ cursor: "pointer" }}
            >
              ...
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                eventKey="2"
                onClick={() => onSendNotificationClick(record)}
              >
                Send Notification
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="1"
                onClick={() => onViewApplicationClick(record)}
              >
                {isEditable ? "View / Edit" : "View"} Application
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    });

    return col;
  };

  const PhoneCellWithSmsTooltip = ({ phone, applicantId }) => {
    const smsPreferenceEnabled = useAppSelector(
      (state) =>
        state.applications.commsPreferencesByApplicant[applicantId]
          ?.smsPreference
    );
    if (!phone) {
      return null;
    }
    return (
      <span>
        {formatPhone(phone)}
        {smsPreferenceEnabled && (
          <Tooltip
            title="Text Enabled"
            showArrow={false}
            overlayInnerStyle={{
              backgroundColor: "#fff",
              color: "#000",
              border: "1px solid #000",
              padding: "3px 2px",
            }}
          >
            <span className="sms-pref-enabled-icon" />
          </Tooltip>
        )}
      </span>
    );
  };

  const prepareData = (applicants: Applicant[]) => {
    const applicantsArray: any = [];
    const guarantorsArray: any = [];
    const len = applicants.length;
    for (let i = 0; i < len; i++) {
      const tData = applicants[i];
      const riskInfo = getRiskLevel(tData);
      const statusId = tData.applicationProcess
        ? tData.applicationProcess.applicationStatusId
        : null;

      var statusName = getApplicantBaseStatusName(tData, statusId, application);
      if (tData.isOnExclusionList && statusName === "Cancelled") {
        statusName = ApplicationStatus.CANCELLED_DUE_TO_EXCLUSION_LIST;
      }
      if (isCancelledDueToHighRisk(application) && statusName === "Cancelled") {
        statusName = ApplicationStatus.CANCELLED_DUE_TO_MANUAL_REVIEW;
      }
      if (isApplicationStatusExpired(statusName, application)) {
        statusName = ApplicationStatus.EXPIRED;
      }
      if (
        isAddendumsEnabled &&
        !hasAddendumResponse &&
        statusId === ApplicationStatusId.LeaseReady
      ) {
        statusName = ApplicationStatusText.PENDING_ADDENDUMS;
      }
      statusName = getActualLeaseStatus(statusName, application);
      const data = {
        id: tData.id,
        fullName: tData.isSoftDelete ? (
          <>
            {`${tData.firstName || ""} ${tData.lastName || ""} `}{" "}
            <Chip color="warning" label="Removed" size="small" />
          </>
        ) : (
          (tData.firstName || "") + " " + (tData.lastName || "")
        ),
        status: statusName,
        emailAddress: tData.emailAddress,
        phoneNumber: tData.phoneNumber,
        lastLoggedIn: "",
        key: `${tData.authenticationId}-${i}`,
        firstName: tData.firstName,
        middleName: tData.middleName,
        lastName: tData.lastName,
        suffix: tData.suffix,
        phoneNumberType: tData.phoneNumberType,
        dob: tData.dob,
        ssn: tData.ssn,
        address1: tData.address1,
        address2: tData.address2,
        city: tData.city,
        state: tData.state,
        zip: tData.zip,
        driverLicense: tData.stateLicenseNumber,
        licenseIssuer: tData.stateLicenseIssuer,
        annualIncome: tData.annualIncome,
        applicationId: application.applicationId,
        employer:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].employer,
        industry:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].industry,
        employerPhone:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].employerPhone,
        employerAddress1:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].address1,
        employerAddress2:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].address2,
        employerCity:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].city,
        employerState:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].state,
        employerZip:
          tData.applicantEmployer &&
          tData.applicantEmployer[0] &&
          tData.applicantEmployer[0].zip,
        gender: getGenderEquivalent(tData.gender),
        householdStatus: tData.householdStatus,
        nonUSPhone: tData.nonUSPhone,
        isSSN: tData.isSSN,
        smsPreference: tData.smsPreference,
        emailPreference: tData.emailPreference,
        isMiddleName: tData.isMiddleName,
        isOnExclusionList: tData.isOnExclusionList,
        exclusionReason: tData.exclusionReason,
        riskAssessmentCancellationTemplate:
          tData.riskAssessmentCancellationTemplate,
        riskLevel: riskInfo.risk,
        riskColor: riskInfo.color,
        riskPercentage: riskInfo.percentage,
        // AP2-6506: sourced from the same GET /application response for a Lease Domain
        // application - onViewApplicationClick uses these instead of separate API calls.
        applicationFeePaymentStatus: tData.applicationFeePaymentStatus,
        tcAcknowledgementDate: tData.tcAcknowledgementDate,
      };
      if (!applicants[i].applicantTypeId) {
        // ignore applicant w/o applicantTypeId
        continue;
      } else if (
        applicants[i].applicantTypeId == ApplicantType.Applicant ||
        applicants[i].applicantTypeId == ApplicantType.CoTenant
      ) {
        applicantsArray.push(data);
      } else {
        guarantorsArray.push(data);
      }
    }

    setApplicantData(applicantsArray);
    setGuarantorsList(guarantorsArray);

    prepColumns(applicantsArray.length);
  };

  const formatData = (userList: any) => {
    const formattedData: any = [];
    userList.map((user: any) => {
      const statusId = user.applicationProcess
        ? user.applicationProcess.applicationStatusId
        : null;
      const data = {
        fullName: (user.firstName || "") + " " + (user.lastName || ""),
        status: getStatusName(statusId),
        emailAddress: user.emailAddress,
        phoneNumber: user.phoneNumber,
        lastLoggedIn: "",
      };
      formattedData.push(data);
    });
    return formattedData;
  };

  const onViewApplicationClick = (record) => {
    // AP2-6506: both proxy to legacy-only endpoints with no Lease Domain equivalent - see
    // setApplicantPaymentAndTermsFromLeaseDomain.
    if (isLeaseDomainApplicationId(String(application?.applicationId ?? ""))) {
      dispatch(
        setApplicantPaymentAndTermsFromLeaseDomain(record, application?.phaseId)
      );
    } else {
      dispatch(getApplicantPaymentStatus(id!, record.id));
      dispatch(getApplicationTermsAndConditions(record.id));
    }
    setModalApplicant(record);
    setModalShow(true);
  };

  const onSendNotificationClick = (record) => {
    const application =
      selectedApplication ?? LocalStorageService.getApplication(id).application;

    const notificationMessage = getNotificationMessageForApplicant(
      application,
      record
    );

    window.open(
      `mailto:${record.emailAddress}?subject=${
        notificationMessage.subject
      }&body=${encodeURI(notificationMessage.body ?? "")}`
    );
  };

  // Indexed once per addendumCatalog change instead of re-filtering the whole catalog on
  // every row render/lookup.
  const pendingAddendumsByApplicantId = useMemo(
    () => indexPendingAddendumsByApplicantId(addendumCatalog),
    [addendumCatalog]
  );

  // record.id can be a Lease Domain string applicant id or a legacy numeric one (see
  // setCommsPreferencesFromLeaseDomainApplicants above) - coerce before matching against
  // addendum.applicantId, which is always a string.
  const getPendingAddendumsForApplicant = (applicantId: string) =>
    pendingAddendumsByApplicantId[applicantId] ?? [];

  const onAnswerAddendumClick = (record) => {
    setAddendumModalApplicant({
      id: String(record.id),
      name: `${record.firstName || ""} ${record.lastName || ""}`.trim(),
    });
    setAddendumModalShow(true);
  };

  return (
    <div id="avb-applicants">
      <ApplicantInformationModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        applicant={modalApplicant}
        communityId={application?.communityId}
        leaseId={application?.leaseId}
        isConfigurationLoaded={isConfigurationLoaded}
      />
      <SubmitAddendumOnBehalfModal
        show={addendumModalShow}
        onHide={() => setAddendumModalShow(false)}
        leaseId={application?.leaseId ?? ""}
        applicantId={addendumModalApplicant.id}
        applicantName={addendumModalApplicant.name}
        addendums={getPendingAddendumsForApplicant(addendumModalApplicant.id)}
        onSubmitted={() => {
          const leaseId = application?.leaseId;
          if (!leaseId) return;
          dispatch(fetchLeaseAddendums(leaseId)).then((addendums) => {
            if (currentLeaseIdRef.current === leaseId) {
              setAddendumCatalog(addendums ?? []);
            }
          });
          if (application?.applicationId) {
            dispatch(getApplicationById(application.applicationId));
          }
        }}
      />
      <Collapse defaultActiveKey={"appinfo"} bordered={false}>
        <Panel header={`APPLICANT INFORMATION`} key="appinfo">
          <Table
            dataSource={applicantData}
            columns={columns}
            pagination={false}
            rowKey={(record) => `${record.id}-${record.status}`}
          />
          <Table
            dataSource={[...guarantorsList]}
            columns={getGuarantorColumns("Guarantors", guarantorsList.length)}
            pagination={false}
            rowKey={(record) => `${record.id}-${record.status}`}
          />
          <Table
            dataSource={dependentsList}
            columns={getGuarantorColumns("Dependents", dependentsList.length)}
            pagination={false}
          />
        </Panel>
      </Collapse>
      <CancelApplicationModal
        selectedApplicant={selectedApplicant}
        selectedApplication={selectedApplication}
        isHighRiskApplication={hasUnresolvedHighRisk(
          selectedApplication,
          configurationData
        )}
        configuration={configurationData}
        onCancel={() => setSelectedApplicant(undefined)}
        type="cancel-application"
      />
    </div>
  );
};

export default Applicants;
